import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import { useClearableDebounce } from '~/lib/hooks/useClearableDebounce';
import { useUpdateNoteMutation } from '~/store/services/notes/notes';
import {
  AttachmentType,
  NoteStatus,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { Model } from 'survey-core';
import { DateTime } from 'luxon';
import { EditorV2State } from '@equip.health/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { buildUpdateNoteRequest } from '~/lib/util/notes/noteUpdateRequests.util';
import { NO_NOTE_TEMPLATE_OPTION } from '~/lib/constants/notes';

type Props = {
  appointmentDetails?: AppointmentDetailResponse;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  setLastSavedTime: Dispatch<SetStateAction<DateTime | null>>;
  hasUnsavedChanges: boolean;
  formValues: EditNoteFormData;
  isGetNoteDataSuccess: boolean;
  noteId: string | null;
  uploadedFiles: AttachmentType[];
  noteTemplateFormModel: Model;
  noteContentEditorRef: MutableRefObject<EditorV2State | null>;
  sessionSummaryEditorRef: MutableRefObject<EditorV2State | null>;
  /**
   * @deprecated try to remove this - should not be necessary, just pull off of drawer context
   */
  patientId?: string;
};
export const useAutosavePatientNote = ({
  appointmentDetails,
  setLastSavedTime,
  setHasUnsavedChanges,
  hasUnsavedChanges,
  isGetNoteDataSuccess,
  noteId,
  uploadedFiles,
  formValues,
  noteTemplateFormModel,
  noteContentEditorRef,
  sessionSummaryEditorRef,
  patientId,
}: Props) => {
  const { currentUser } = useGlobalContext();
  const { sessionSummaryRichText: isSessionSummaryRichTextEnabled } =
    useFlags<LDFlags>();
  const updateFnRef = useRef(() => {});
  const { drawerStatus } = useEditNoteDrawerContext();
  const { clearDebounce: clearAutosaveDebounce, debouncedFn } =
    useClearableDebounce(6000);

  const [
    updateNoteMutation,
    {
      error: autosaveError,
      isLoading: isAutosaveNoteLoading,
      reset: resetAutosaveError,
    },
  ] = useUpdateNoteMutation();

  useEffect(() => {
    if (hasUnsavedChanges) {
      debouncedFn(() => {
        updateFnRef.current();
      });
    }
  }, [hasUnsavedChanges, JSON.stringify(formValues)]);

  useEffect(() => {
    if (drawerStatus === 'closed' || drawerStatus === 'minimized') {
      clearAutosaveDebounce();
    }
  }, [drawerStatus]);

  useEffect(() => {
    updateFnRef.current = () => {
      if (isGetNoteDataSuccess && hasUnsavedChanges && formValues) {
        const uploadedFilesMetadata = uploadedFiles.map((file) => ({
          attachmentExternalId: file.attachmentExternalId,
          fileName: file.fileName,
          statusCode: file.statusCode,
        }));

        const hasTemplateSelected =
          formValues.noteTemplateRevision?.node &&
          formValues.noteTemplateRevision?.node !==
            NO_NOTE_TEMPLATE_OPTION.node;

        updateNoteMutation({
          body: buildUpdateNoteRequest({
            appointmentExternalId: appointmentDetails?.appointmentExternalId,
            authoredByProviderExternalId: currentUser.userId,
            formValues,
            noteId,
            noteStatus: NoteStatus.DRAFT,
            patientId,
            uploadedFiles: uploadedFilesMetadata,
            noteTemplateRevisionData: hasTemplateSelected
              ? noteTemplateFormModel.data
              : null,
            noteContentEditorRef,
            sessionSummaryEditorRef: isSessionSummaryRichTextEnabled
              ? sessionSummaryEditorRef
              : undefined,
          }),
          options: { skipInvalidatingGetNote: true },
        }).then(() => {
          setLastSavedTime(DateTime.now());
          setHasUnsavedChanges(false);
        });
      }
    };
  }, [
    formValues,
    appointmentDetails,
    hasUnsavedChanges,
    isGetNoteDataSuccess,
    setHasUnsavedChanges,
    uploadedFiles,
    noteTemplateFormModel,
    currentUser,
    noteContentEditorRef,
    sessionSummaryEditorRef,
    isSessionSummaryRichTextEnabled,
  ]);

  return {
    clearAutosaveDebounce,
    isAutosaveNoteLoading,
    autosaveError,
    resetAutosaveError,
    debouncedUpdateNote: () => debouncedFn(() => updateFnRef.current()),
  };
};
